import { rem } from '@visiba/std';
import { motion } from 'framer-motion';
import { useRef } from 'react';
import { DismissButton, FocusScope, useOverlay } from 'react-aria';
import styled from 'styled-components';

interface Props {
	popoverRef?: React.RefObject<HTMLDivElement>;
	children: React.ReactNode;
	isOpen?: boolean;
	onClose?: () => void;
}

const elements = {
	container: styled(motion.div)`
		position: absolute;
		z-index: 1;
		left: -${rem(25)};
		top: calc(100% + ${rem(8)});
	`,
};

export function Popover(props: Props): JSX.Element {
	const ref = useRef<HTMLDivElement>(null);
	const { popoverRef = ref, isOpen, onClose, children } = props;

	// Handle events that should cause the popup to close,
	// e.g. blur, clicking outside, or pressing the escape key.
	const { overlayProps } = useOverlay(
		{
			isOpen,
			onClose,
			shouldCloseOnBlur: true,
			isDismissable: true,
		},
		popoverRef,
	);

	return (
		<FocusScope restoreFocus>
			<elements.container
				{
					/* Reason: Miss-match with React-Aria and Styled Components... */
					/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
					// biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration - should be fixed
					...(overlayProps as any)
				}
				ref={popoverRef}
				transition={{
					type: 'tween',
				}}
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: 10 }}
			>
				{children}

				{/**
					Add a hidden <DismissButton> component at the end of the popover
					to allow screen reader users to dismiss the popup easily.
				*/}
				<DismissButton onDismiss={onClose} />
			</elements.container>
		</FocusScope>
	);
}
